import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    User,
    BookOpen,
    DollarSign,
    TrendingUp,
    Award,
    Calendar,
    CheckCircle,
    Clock,
    FileText
} from 'lucide-react';

export default function StudentProgressTracking() {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const [studentData, setStudentData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (studentId) {
            fetchStudentProgress();
        }
    }, [studentId]);

    const fetchStudentProgress = async () => {
        try {
            setLoading(true);
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
            const token = localStorage.getItem('token');

            const response = await fetch(
                `${API_BASE_URL}/tracking/students/${studentId}/progress`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) throw new Error('Failed to fetch student progress');

            const data = await response.json();
            setStudentData(data);
        } catch (error) {
            console.error('Error fetching student progress:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!studentData) {
        return (
            <div className="p-6">
                <p>Student not found</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="w-full mx-auto">
                {/* Header */}
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                </button>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                                {studentData.student.name.charAt(0)}
                            </div>
                            <div className="ml-4">
                                <h1 className="text-2xl font-bold text-gray-900">{studentData.student.name}</h1>
                                <p className="text-gray-600">{studentData.student.email}</p>
                                <p className="text-sm text-gray-500">Admission: {studentData.student.admissionNumber}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-gray-600">Course</p>
                            <p className="text-lg font-semibold text-gray-900">{studentData.student.courseName}</p>
                            <p className="text-sm text-gray-500">{studentData.student.campusName}</p>
                        </div>
                    </div>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Units</p>
                                <p className="text-3xl font-bold text-gray-900">{studentData.metrics.totalUnits}</p>
                            </div>
                            <BookOpen className="h-8 w-8 text-blue-600" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Completed</p>
                                <p className="text-3xl font-bold text-green-600">{studentData.metrics.completedUnits}</p>
                            </div>
                            <CheckCircle className="h-8 w-8 text-green-600" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Average Grade</p>
                                <p className="text-3xl font-bold text-indigo-600">{studentData.metrics.averageGrade}%</p>
                            </div>
                            <TrendingUp className="h-8 w-8 text-indigo-600" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Attendance</p>
                                <p className="text-3xl font-bold text-purple-600">{studentData.metrics.attendanceRate}%</p>
                            </div>
                            <Calendar className="h-8 w-8 text-purple-600" />
                        </div>
                    </div>
                </div>

                {/* Course Progress */}
                {studentData.progress && (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
                        <h2 className="text-xl font-semibold mb-4">Course Progress</h2>
                        <div className="space-y-4">
                            <div>
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                                    <span className="text-sm font-bold text-indigo-600">{studentData.progress.progressPercentage}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3">
                                    <div
                                        className="bg-indigo-600 h-3 rounded-full transition-all"
                                        style={{ width: `${studentData.progress.progressPercentage}%` }}
                                    ></div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                <div>
                                    <p className="text-sm text-gray-600">Last Activity</p>
                                    <p className="font-semibold">{studentData.progress.lastActivityDate
                                        ? new Date(studentData.progress.lastActivityDate).toLocaleDateString()
                                        : 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Assignment Completion</p>
                                    <p className="font-semibold">{studentData.progress.assignmentCompletionRate}%</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Unit Enrollments */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">Unit Enrollments</h2>
                        <div className="space-y-3">
                            {studentData.enrollments.map((enrollment) => (
                                <div key={enrollment.unitId} className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <h3 className="font-semibold text-gray-900">{enrollment.unitName}</h3>
                                            <p className="text-sm text-gray-600">{enrollment.courseName}</p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${enrollment.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                enrollment.status === 'active' ? 'bg-blue-100 text-blue-800' :
                                                    'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                {enrollment.status}
                                            </span>
                                            {enrollment.grade && (
                                                <span className="px-3 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                                    Grade: {enrollment.grade}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Payments */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
                    <div className="p-6">
                        <h2 className="text-xl font-semibold mb-4">Payment History</h2>
                        <div className="space-y-3">
                            {studentData.payments.map((payment) => (
                                <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div>
                                        <p className="font-medium text-gray-900">{payment.unitName}</p>
                                        <p className="text-sm text-gray-600">{payment.paymentMethod.toUpperCase()}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-semibold text-gray-900">KES {payment.amount}</p>
                                        <span className={`text-xs px-2 py-1 rounded-full ${payment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                            payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                            {payment.status}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Certificates */}
                {studentData.certificates.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                        <div className="p-6">
                            <h2 className="text-xl font-semibold mb-4">Certificates</h2>
                            <div className="space-y-3">
                                {studentData.certificates.map((cert) => (
                                    <div key={cert.id} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-semibold text-gray-900">{cert.courseName}</p>
                                                <p className="text-sm text-gray-600">Certificate No: {cert.certificateNumber}</p>
                                                <p className="text-sm text-gray-600">Grade: {cert.grade}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm text-gray-600">
                                                    {new Date(cert.issueDate).toLocaleDateString()}
                                                </p>
                                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                                    {cert.status}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
